<template>
  <div id="survey_detail">
    <Navbar current="Pesquisas" :link="$paths.surveys.list" linkName="Pesquisas" />
    <Sidebar />
    <Container col="col-12">
      <Header :isSubHeader="false" :title="survey ? survey.title : 'Pesquisa'">
        <template v-slot:button v-if="survey">
          <div class="d-flex gap-2">
            <template v-if="isDraft">
              <button class="btn btn-outline-danger" @click="deleteSurvey">
                <i class="fa-solid fa-trash me-1"></i>Excluir
              </button>
              <button class="btn btn-outline-secondary" @click="editSurvey">
                <i class="fa-solid fa-pen me-1"></i>Editar
              </button>
              <button
                class="btn btn-primary"
                :disabled="sending || !survey.audience_count || !survey.questions.length"
                @click="sendSurvey"
              >
                <span v-if="sending" class="spinner-border spinner-border-sm me-1"></span>
                <i v-else class="fa-solid fa-paper-plane me-1"></i>Enviar
              </button>
            </template>
            <button v-if="isSent" class="btn btn-outline-danger" :disabled="closing" @click="closeSurvey">
              <span v-if="closing" class="spinner-border spinner-border-sm me-1"></span>
              Encerrar
            </button>
          </div>
        </template>
      </Header>

      <LoadingContainer v-if="loading && !survey" />

      <div v-else-if="survey">
        <!-- Cabeçalho comum -->
        <div class="card mb-3">
          <div class="card-body d-flex flex-wrap align-items-center gap-4">
            <div>
              <small class="text-muted d-block">Status</small>
              <span class="badge fs-6" :class="statusClass(survey.status)">
                {{ statusLabel(survey.status) }}
              </span>
            </div>
            <div>
              <small class="text-muted d-block">Público</small>
              <strong>{{ survey.audience_count }}</strong>
              <span class="text-muted small"> {{ isDraft ? "(estimativa)" : "receberam" }}</span>
            </div>
            <div v-if="!isDraft">
              <small class="text-muted d-block">Respostas</small>
              <strong>{{ survey.answered }}</strong>
              <span class="text-muted small"> ({{ responseRate }}%)</span>
            </div>
            <div v-if="survey.sent_at">
              <small class="text-muted d-block">Enviada em</small>
              <span>{{ formatDate(survey.sent_at) }}</span>
            </div>
            <div v-if="survey.closed_at">
              <small class="text-muted d-block">Encerrada em</small>
              <span>{{ formatDate(survey.closed_at) }}</span>
            </div>
          </div>
          <div class="card-body border-top pt-3">
            <small class="text-muted d-block mb-1">Público filtrado</small>
            {{ audienceSummary() }}
          </div>
          <div v-if="survey.description" class="card-body border-top pt-3">
            <small class="text-muted d-block mb-1">Descrição</small>
            {{ survey.description }}
          </div>
        </div>

        <!-- Prévia: lista de perguntas somente leitura -->
        <div v-if="isDraft" class="card">
          <div class="card-header"><strong>Perguntas</strong></div>
          <div class="card-body">
            <SurveyQuestionsList :questions="survey.questions" />
          </div>
        </div>

        <!-- Detalhe: envios + resultados -->
        <div v-else class="card">
          <ul class="nav nav-tabs px-3 pt-2" role="tablist">
            <li class="nav-item">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'results' }"
                @click="selectTab('results')"
              >
                Resultados
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'delivery' }"
                @click="selectTab('delivery')"
              >
                Status de envio
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'questions' }"
                @click="selectTab('questions')"
              >
                Perguntas
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'recipients' }"
                @click="selectTab('recipients')"
              >
                Envios
              </button>
            </li>
          </ul>
          <div class="card-body">
            <SurveyResults
              v-show="activeTab === 'results'"
              v-if="resultsLoaded || activeTab === 'results'"
              :uuid="uuid"
            />
            <div v-show="activeTab === 'delivery'">
              <div class="row g-3 text-center mb-4">
                <div class="col-6 col-md-3">
                  <small class="text-muted d-block">Destinatários</small>
                  <strong class="fs-4">{{ survey.delivery?.recipients }}</strong>
                </div>
                <div class="col-6 col-md-3">
                  <small class="text-muted d-block">Enviados</small>
                  <strong class="fs-4">{{ survey.delivery?.sent }}</strong>
                </div>
                <div class="col-6 col-md-3">
                  <small class="text-muted d-block">Pendentes</small>
                  <strong class="fs-4">{{ survey.delivery?.pending }}</strong>
                </div>
                <div class="col-6 col-md-3">
                  <small class="text-muted d-block">Sem eventos</small>
                  <strong class="fs-4">{{ survey.delivery?.no_events }}</strong>
                </div>
              </div>

              <h6 class="text-muted mb-3">Funil de entrega</h6>
              <div class="row g-3 text-center mb-4">
                <div class="col-6 col-md-3">
                  <small class="text-muted d-block">Aceitos pelo SendGrid</small>
                  <strong class="fs-5">{{ survey.delivery?.processed }}</strong>
                </div>
                <div class="col-6 col-md-3">
                  <small class="text-muted d-block">Entregues</small>
                  <strong class="fs-5">{{ survey.delivery?.delivered }}</strong>
                  <span class="text-success small d-block">{{ survey.delivery?.delivered_rate }}%</span>
                </div>
                <div class="col-6 col-md-3">
                  <small class="text-muted d-block">Abertos</small>
                  <strong class="fs-5">{{ survey.delivery?.opened }}</strong>
                  <span class="text-success small d-block">{{ survey.delivery?.open_rate }}%</span>
                </div>
                <div class="col-6 col-md-3">
                  <small class="text-muted d-block">Clicaram no link</small>
                  <strong class="fs-5">{{ survey.delivery?.clicked }}</strong>
                </div>
              </div>

              <h6 class="text-muted mb-3">Problemas</h6>
              <div class="row g-3 text-center">
                <div class="col-6 col-md-2">
                  <small class="text-muted d-block">Não entregues</small>
                  <strong class="fs-5" :class="{ 'text-danger': survey.delivery?.bounced }">
                    {{ survey.delivery?.bounced }}
                  </strong>
                </div>
                <div class="col-6 col-md-2">
                  <small class="text-muted d-block">Descartados</small>
                  <strong class="fs-5" :class="{ 'text-danger': survey.delivery?.dropped }">
                    {{ survey.delivery?.dropped }}
                  </strong>
                </div>
                <div class="col-6 col-md-2">
                  <small class="text-muted d-block">Marcado como spam</small>
                  <strong class="fs-5" :class="{ 'text-danger': survey.delivery?.spam }">
                    {{ survey.delivery?.spam }}
                  </strong>
                </div>
                <div class="col-6 col-md-2">
                  <small class="text-muted d-block">Adiados</small>
                  <strong class="fs-5" :class="{ 'text-warning': survey.delivery?.deferred }">
                    {{ survey.delivery?.deferred }}
                  </strong>
                </div>
                <div class="col-6 col-md-2">
                  <small class="text-muted d-block">Falharam</small>
                  <strong class="fs-5" :class="{ 'text-danger': survey.delivery?.failed }">
                    {{ survey.delivery?.failed }}
                  </strong>
                </div>
              </div>
            </div>
            <div v-show="activeTab === 'questions'">
              <SurveyQuestionsList :questions="survey.questions" />
            </div>
            <SurveyRecipients
              v-show="activeTab === 'recipients'"
              v-if="recipientsLoaded || activeTab === 'recipients'"
              :uuid="uuid"
            />
          </div>
        </div>
      </div>
    </Container>
  </div>
</template>

<script>
import SurveyDetailController from "./SurveyDetailController";
export default SurveyDetailController;
</script>
